import { EntityCustomViewParams, useAuthController, useDataSource } from "@firecms/core";
import { Alert, Button, Typography } from "@firecms/ui";
import { useEffect, useState } from "react";

import { hasAnyRole } from "./apple_delegated_onboarding.roles";
import {
  AppleDelegatedOnboardingEntity,
  OnboardingShowcaseRequestEntity,
} from "./apple_delegated_onboarding.types";
import { resolveDocumentEnvironment } from "./apple_delegated_onboarding.utils";
import { fetchOnboardingParent } from "./apple_delegated_onboarding.write";
import { SHOWCASE_LIFECYCLE_ROLES } from "./apple_showcase_lifecycle";
import {
  SHOWCASE_LIFECYCLE_ACTION_LABELS,
  SHOWCASE_LIFECYCLE_ACTION_ORDER,
  ShowcaseLifecycleActionEnum,
} from "./apple_showcase_lifecycle.types";
import { describeSubject } from "./apple_showcase_lifecycle.utils";
import { ShowcaseLifecycleDialog } from "./ShowcaseLifecycleDialog";

// The "Lifecycle" tab on one showcase request: the three actions, each behind its own confirmation.
//
// ⚠️ IT IS A TAB, NOT A ROW ACTION, AND THAT IS FORCED. `EntityAction.onClick` in FireCMS
// 3.0.0-beta.14 cannot render a dialog (the same beta limitation `apple_grant_status.ts:28-31` and
// `apple_delegated_onboarding_showcases.ts` both document from the other direction — no `isEnabled`
// predicate either). A destructive action REQUIRES a two-step confirmation, so it has to live
// somewhere that can hold React state. A snackbar-only row action would be one click from a permanent
// change, which is the thing this ticket exists to prevent.
//
// ⚠️ DEACTIVATE IS RENDERED FIRST. `SHOWCASE_LIFECYCLE_ACTION_ORDER` decides that, not this file, so
// the ordering is a recorded decision rather than the order someone happened to type the buttons in:
// deactivating stops the display, deleting also destroys the history, so the safe option is offered
// first.

export function ShowcaseLifecycleView({
  entity,
}: EntityCustomViewParams<Partial<OnboardingShowcaseRequestEntity>>) {
  const dataSource = useDataSource();
  const authController = useAuthController();
  const [parent, setParent] = useState<Partial<AppleDelegatedOnboardingEntity> | null>(null);
  const [openAction, setOpenAction] = useState<ShowcaseLifecycleActionEnum | null>(null);
  const childPath = entity?.path;

  useEffect(() => {
    let cancelled = false;
    void fetchOnboardingParent(dataSource, childPath).then((values) => {
      if (!cancelled) setParent(values);
    });
    return () => {
      cancelled = true;
    };
  }, [dataSource, childPath]);

  if (!entity) {
    return (
      <div className="p-8">
        <Alert color="info">
          Save this showcase request first. There is nothing at Apple to act on until it exists.
        </Alert>
      </div>
    );
  }

  if (!hasAnyRole(authController, SHOWCASE_LIFECYCLE_ROLES)) {
    return (
      <div className="p-8">
        <Alert color="info">
          You can see this showcase but not change it at Apple. Extending, deactivating and deleting
          a showcase are irreversible, so they are limited to the same roles that may queue an Apple
          write.
        </Alert>
      </div>
    );
  }

  // The environment comes from the PARENT document, which FireCMS does not hand a subcollection —
  // hence the fetch above.
  //
  // ⚠️ A FAILED READ DEGRADES TO `null`, WHICH DISABLES EVERY BUTTON, and that is the right direction
  // for a destructive surface: with no environment there is no env-scoped showcase id, so
  // `describeSubject` returns nothing to act on. Defaulting to an environment instead would put a
  // Delete button next to an id that might belong to the other one.
  const env = resolveDocumentEnvironment(parent);
  const subject = describeSubject(entity.values, env);

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex flex-col gap-1">
        <Typography variant="subtitle2">Showcase lifecycle</Typography>
        <Typography variant="body2">
          These act on the showcase Apple is holding for this record, in the{" "}
          <strong>{env ?? "not yet known"}</strong> environment.{" "}
          <strong>None of them can be undone.</strong> Apple provides an &ldquo;undelete&rdquo; for
          location assets but not for showcases, and a deactivated showcase cannot be switched back
          on.
        </Typography>
      </div>

      {!subject.showcaseId && (
        <Alert color="warning">
          No Apple showcase id is recorded for {env ?? "any environment"}, so there is nothing to
          act on yet. If the showcase was queued, wait for the result to appear on the Queue tab.
        </Alert>
      )}

      <div className="flex flex-col gap-3">
        {SHOWCASE_LIFECYCLE_ACTION_ORDER.map((action) => (
          <div key={action} className="flex items-center gap-3">
            <Button
              color={action === ShowcaseLifecycleActionEnum.EXTEND ? "primary" : "error"}
              variant={action === ShowcaseLifecycleActionEnum.DELETE ? "outlined" : "filled"}
              disabled={!subject.showcaseId}
              onClick={() => setOpenAction(action)}
            >
              {SHOWCASE_LIFECYCLE_ACTION_LABELS[action]}
            </Button>
            <Typography variant="caption">{ACTION_HINTS[action]}</Typography>
          </div>
        ))}
      </div>

      <Alert color="info">
        Nothing here calls Apple directly. Each button records a request; the backend then reads the
        showcase back from Apple, checks that Apple actually permits the action, and writes the
        result into this record&apos;s Lifecycle history. If it refuses, nothing was changed.
      </Alert>

      {openAction && (
        <ShowcaseLifecycleDialog
          open
          onOpenChange={(open) => {
            if (!open) setOpenAction(null);
          }}
          action={openAction}
          subject={subject}
          showcaseRequestPath={entity.path}
          showcaseRequestId={entity.id}
        />
      )}
    </div>
  );
}

/**
 * One line per action, naming the difference that matters.
 *
 * ⚠️ THE DEACTIVATE AND DELETE HINTS MUST STAY DISTINCT. Both are permanent; only one destroys the
 * record of what ran. An operator choosing between them needs that difference on screen before they
 * open either dialog.
 */
const ACTION_HINTS: Record<ShowcaseLifecycleActionEnum, string> = {
  [ShowcaseLifecycleActionEnum.DEACTIVATE]:
    "Recommended. Stops it displaying and keeps the record. Permanent — it cannot be switched back on. Apple only allows this while the showcase is live.",
  [ShowcaseLifecycleActionEnum.EXTEND]:
    "Lets it run longer. Apple measures the limit from the showcase's ORIGINAL start date, not from today.",
  [ShowcaseLifecycleActionEnum.DELETE]:
    "Removes it AND its history. No undelete exists. Apple refuses to delete a live showcase — deactivate that instead.",
};
